import { NgStyle } from '@angular/common'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSpinner, faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable } from 'rxjs'

import { CompanyDamageReport, DamageKind, FeatureType, ListItem, RepositoryModal } from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { CompanyDamageReportsComponent } from './company-damage-reports.component'

@Component({
    templateUrl: './company-damage-report.component.html',
    styleUrls: ['./safety-checklist-modal.css'],
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle, VoiceTextFieldComponent],
})
export class CompanyDamageReportComponent extends RepositoryModal<CompanyDamageReport> {
    faX = faX
    faSpinner = faSpinner

    userList: ListItem<string>[] = []
    damageKindList: ListItem<string>[] = [
        new ListItem<string>(DamageKind.Equipment, 'Equipment'),
        new ListItem<string>(DamageKind.Vehicle, 'Vehicle'),
    ]
    dotClassificationList: ListItem<string>[] = [new ListItem('DOT', 'DOT'), new ListItem('Non-DOT', 'Non-DOT')]

    attachments: any[] = []
    progress = 0

    protected onInitialized(): void {
        const parent = this.editOptions.initializeParams as CompanyDamageReportsComponent
        this.userList = [...(parent.activeUserList ?? [])]

        const currentUserId = this.target.reportedByUserId
        if (currentUserId && !this.userList.some(u => u.value === currentUserId)) {
            const inactive = parent.allUsers.find(u => u.id === currentUserId)
            const label = inactive ? `${inactive.displayName} (inactive)` : `${currentUserId} (inactive)`
            this.userList = [new ListItem(currentUserId, label), ...this.userList]
        }

        if (this.isAdding && !this.target.reportedByUserId) {
            const loggedInUserId = parent.currentUserId
            if (loggedInUserId && this.userList.some(u => u.value === loggedInUserId)) {
                this.target.reportedByUserId = loggedInUserId
            }
        }
    }

    get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyChecklistEdit])
    }

    get isVehicle(): boolean {
        return this.target.damageKind === DamageKind.Vehicle
    }

    get generalSectionTitle(): string {
        return this.isVehicle
            ? 'Company Vehicle Damage - General Information'
            : 'Company Equipment Damage - General Information'
    }

    get detailsSectionTitle(): string {
        return this.isVehicle
            ? 'Company Vehicle Damage - Detailed Information'
            : 'Company Equipment Damage - Detailed Information'
    }

    get assetNumberLabel(): string {
        return this.isVehicle ? 'Vehicle #' : 'Equipment #'
    }

    removeAttachment(file: any) {
        this.attachments.remove(file)
    }

    async onFileChanged(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input.files
        if (!files) return

        const progress = (p: number) => {
            this.progress = p
        }

        for (const file of Array.from(files)) {
            if (file.type.startsWith('image/')) {
                const options: Options = {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    onProgress: progress,
                }
                const compressedFile = await imageCompression(file, options)
                compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
                this.attachments.push(compressedFile)
            } else {
                const docFile = file as any
                docFile.url = './Resources/pdfImage.png'
                this.attachments.push(docFile)
            }
        }

        input.value = ''
    }

    isDocument(url: string): boolean {
        const lower = url.toLowerCase()
        return ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'].some(ext => lower.endsWith(ext))
    }

    getExistingFileSrc(url: string): string {
        if (this.isDocument(url)) {
            return './Resources/pdfImage.png'
        }
        return './Resources/Images/SafetyChecklist/' + url + '?w=300'
    }

    getPendingFileSrc(file: any): string {
        if (file.type === 'application/pdf' || (file.name && this.isDocument(file.name))) {
            return './Resources/pdfImage.png'
        }
        return file.url
    }

    displayFileName(index: number): string {
        return this.target.imageFileNames?.[index] ?? 'Attachment'
    }

    openExistingFile(url: string) {
        window.open('./Resources/Images/SafetyChecklist/' + url, '_blank')
    }

    private uploadAttachments(id: string): Observable<any> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.attachments.length; i++) {
            uploadData.append('myFile' + i, this.attachments[i], this.attachments[i].name)
        }
        return this.services.data.post('api/CompanyDamageReport/UploadImages', uploadData)
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.attachments.length === 0) {
                    resolveSave(successResult)
                    return
                }

                this.uploadAttachments(successResult.id).subscribe({
                    next: () => resolveSave(successResult),
                    error: errorResult => rejectSave(errorResult),
                })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }
}
